/* eslint-disable no-magic-numbers */
import { CircleLayer } from 'mapbox-gl';

import { SymbolLayerWithMapIcon } from '../../components/MapIcon';
import {
  POINTS_OF_INTEREST_ICON_SIZE,
  POINTS_OF_INTEREST_MINZOOM,
  POINTS_OF_INTEREST_SIMPLIFIED_MINZOOM,
  POINTS_OF_INTEREST_SIMPLIFIED_CIRCLE_RADIUS,
  POINTS_OF_INTEREST,
  PointOfInterest,
  PointsOfInterest,
  ALL_POI_TOGGLEABLE_GROUPS,
} from '../../constants/mapLayers/pointsOfInterest';
import { LayerWithToggleableGroup } from '../../types/types';
import { SOURCE } from '../sources';

/* NB. The 'source-layer' value needs to match the value set in the mapbox tileset explorer for the tilset (source) you are using */

type PointOfInterestIconLayers = Record<
  keyof PointsOfInterest,
  LayerWithToggleableGroup & SymbolLayerWithMapIcon
>;
type PointOfInterestSimplifiedLayers = Record<
  keyof PointsOfInterest,
  LayerWithToggleableGroup & CircleLayer
>;

const generatePointOfInterestIconLayer = ({
  layerId,
  icon,
  typeId,
  toggleableGroup,
}: PointOfInterest): LayerWithToggleableGroup & SymbolLayerWithMapIcon => ({
  id: layerId,
  type: 'symbol',
  source: SOURCE.pointsOfInterest.name,
  'source-layer': 'points_of_interest',
  minzoom: POINTS_OF_INTEREST_MINZOOM,
  layout: {
    visibility: 'visible',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    'icon-image': icon,
    'icon-size': POINTS_OF_INTEREST_ICON_SIZE,
  },
  filter: ['==', 'type', typeId],
  toggleableGroups: [toggleableGroup],
});

const generatePOISimplifiedLayer = ({
  background,
  layerId,
  typeId,
  toggleableGroup,
}: PointOfInterest): LayerWithToggleableGroup & CircleLayer => ({
  id: `${layerId}_simplified_layer`,
  type: 'circle',
  source: SOURCE.pointsOfInterest.name,
  'source-layer': 'points_of_interest',
  minzoom: POINTS_OF_INTEREST_SIMPLIFIED_MINZOOM,
  maxzoom: POINTS_OF_INTEREST_MINZOOM,
  paint: {
    'circle-color': background,
    'circle-radius': POINTS_OF_INTEREST_SIMPLIFIED_CIRCLE_RADIUS,
    'circle-stroke-color': '#FFFFFF',
    'circle-stroke-width': 1,
  },
  filter: ['==', 'type', typeId],
  toggleableGroups: [toggleableGroup],
});

export const POINTS_OF_INTEREST_HIGHLIGHT_LAYER: LayerWithToggleableGroup & SymbolLayerWithMapIcon =
  {
    id: 'points_of_interest_highlight_layer',
    type: 'symbol',
    source: SOURCE.pointsOfInterest.name,
    'source-layer': 'points_of_interest',
    minzoom: POINTS_OF_INTEREST_SIMPLIFIED_MINZOOM,
    layout: {
      visibility: 'visible',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'icon-image': 'highlight',
      'icon-size': ['step', ['zoom'], 0.7, 8, 0.7, 9, 0.7, 11, 0.8],
    },
    filter: ['==', 'id', ''],
    toggleableGroups: ALL_POI_TOGGLEABLE_GROUPS,
  };

export const POINTS_OF_INTEREST_LAYERS = Object.entries(POINTS_OF_INTEREST).reduce(
  (poiLayers, [poiKey, poiValue]) => ({
    ...poiLayers,
    [poiKey]: generatePointOfInterestIconLayer(poiValue),
  }),
  {},
) as PointOfInterestIconLayers;

export const POINTS_OF_INTEREST_SIMPLIFIED_LAYER = Object.entries(POINTS_OF_INTEREST).reduce(
  (pointsOfInterestLayers, [pointOfInterestKey, pointOfInterestValue]) => ({
    ...pointsOfInterestLayers,
    [pointOfInterestKey]: generatePOISimplifiedLayer(pointOfInterestValue),
  }),
  {},
) as PointOfInterestSimplifiedLayers;
